import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, GitBranch, Eye, Repeat, Network, ShoppingBag, IndianRupee, CreditCard, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useBranches, STATUS_STYLE, STATUS_LABEL, inr } from "@/lib/branch";
import { Skeleton } from "@/components/ui/skeleton";
import { ShareLinks } from "@/components/ShareLinks";
import { CreateBranchDialog } from "@/components/CreateBranchDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const CARDS = [
  ["total_branches", "Total Owned Branches"], ["active_branches", "Active Branches"], ["pending_branches", "Pending Branches"],
  ["suspended_branches", "Suspended Branches"], ["direct_customers", "Direct Customers"], ["unique_downline", "Unique Downline Members"],
  ["verified_orders", "Verified Product Orders"], ["eligible_sales", "Eligible Sales Volume", true], ["earned_commission", "Earned Commissions", true],
  ["held_commission_events", "Held Commission Events"], ["wallet_balance", "Available Wallet", true], ["total_withdrawn", "Total Withdrawals", true],
];

export default function MyBranches() {
  const { user } = useAuth();
  const { branches, loading, refresh, setSelected } = useBranches();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelId, setCancelId] = useState(null);

  const load = () => { api.get("/branches/summary").then(({ data }) => setSummary(data)); refresh(); };
  useEffect(() => { api.get("/branches/summary").then(({ data }) => setSummary(data)).catch(() => toast.error("Could not load summary")); }, []);

  const pay = async (b) => {
    try { const { data } = await api.post(`/branches/${b.id}/pay`); navigate(`/checkout/direct/${data.order_id}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); }
  };
  const cancel = async () => {
    try { await api.post(`/branches/${cancelId}/cancel`); toast.success("Pending branch cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
    setCancelId(null);
  };
  const switchTo = (b) => { setSelected(b.id); toast.success(`Switched to ${b.referral_code}`); };

  return (
    <div className="space-y-8" data-testid="my-branches-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#7F1D1D]">My Branches</h1>
          <p className="text-[#64748B] text-sm">All your referral branches under one login. Every branch keeps your original sponsor.</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="create-branch-btn"><Plus className="w-4 h-4" /> Create New Branch</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="branch-summary-cards">
        {CARDS.map(([k, label, money]) => (
          <div key={k} className="bg-white border border-[#F1F5F9] rounded-2xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-[#94A3B8]">{label}</div>
            {summary ? <div className="font-numbers text-xl font-bold text-[#7F1D1D] mt-1" data-testid={`summary-${k}`}>{money ? inr(summary[k]) : summary[k]}</div> : <Skeleton className="h-7 w-20 mt-1" />}
          </div>
        ))}
      </div>

      {loading ? <div className="grid md:grid-cols-2 gap-4">{[0, 1].map((i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}</div> : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="branch-list">
          {branches.filter((b) => b.status !== "cancelled").map((b) => <BranchCard key={b.id} b={b} onPay={pay} onCancel={setCancelId} onSwitch={switchTo} />)}
        </div>
      )}

      <CreateBranchDialog open={createOpen} onOpenChange={setCreateOpen} userName={user?.name} />
      <AlertDialog open={!!cancelId} onOpenChange={(o) => !o && setCancelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Cancel this pending branch?</AlertDialogTitle>
            <AlertDialogDescription>The branch and its unpaid hamper order will be cancelled. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="cancel-branch-dismiss">Keep</AlertDialogCancel>
            <AlertDialogAction onClick={cancel} className="bg-[#DC2626]" data-testid="cancel-branch-confirm">Cancel branch</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div><div className="text-[10px] uppercase text-[#94A3B8]">{label}</div><div className="font-numbers font-semibold text-[#7F1D1D]">{value}</div></div>
);

const BranchCard = ({ b, onPay, onCancel, onSwitch }) => {
  const link = (tab) => `/branches/${b.id}?tab=${tab}`;
  const act = "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-[#E2E8F0] text-[#334155] hover:border-[#DC2626] hover:text-[#7F1D1D] transition-colors";
  return (
    <div className="bg-white border border-[#F1F5F9] rounded-2xl p-5 space-y-4 hover:shadow-md transition-shadow" data-testid={`branch-card-${b.referral_code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] flex items-center justify-center"><GitBranch className="w-5 h-5 text-[#DC2626]" /></div>
          <div>
            <div className="font-display font-bold text-[#7F1D1D]">{b.name}{b.is_primary && <span className="ml-2 text-[10px] text-[#B91C1C]">MAIN</span>}</div>
            <div className="text-xs text-[#64748B] font-numbers">{b.branch_code} · <b>{b.referral_code}</b></div>
          </div>
        </div>
        <span className={`text-[11px] px-2 py-1 rounded-lg border ${STATUS_STYLE[b.status]}`} data-testid={`branch-status-${b.referral_code}`}>{STATUS_LABEL[b.status]}</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Activated" value={b.activated_at ? new Date(b.activated_at).toLocaleDateString("en-IN") : "—"} />
        <Stat label="Direct" value={b.direct_referrals} /><Stat label="Downline" value={b.downline_members} />
        <Stat label="Verified orders" value={b.verified_orders} /><Stat label="Eligible sales" value={inr(b.eligible_sales)} /><Stat label="Commission" value={inr(b.earned_commission)} />
      </div>
      {b.status === "active" && <ShareLinks code={b.referral_code} compact />}
      {b.status === "pending_payment" && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800" data-testid={`branch-payment-status-${b.referral_code}`}>
          Payment status: <b>{b.pending_order ? b.pending_order.payment_status : "no order yet"}</b>{b.pending_order?.uropay_status ? ` (${b.pending_order.uropay_status})` : ""}{b.pending_order ? ` · ${b.pending_order.order_code} · ${inr(b.pending_order.total)}` : ""}. Referral link unlocks after verified payment.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Link to={link("overview")} className={act} data-testid={`branch-view-${b.referral_code}`}><Eye className="w-3.5 h-3.5" /> View</Link>
        {b.status === "active" && <button onClick={() => onSwitch(b)} className={act} data-testid={`branch-switch-${b.referral_code}`}><Repeat className="w-3.5 h-3.5" /> Switch</button>}
        <Link to={link("network")} className={act} data-testid={`branch-network-${b.referral_code}`}><Network className="w-3.5 h-3.5" /> Network</Link>
        <Link to={link("orders")} className={act} data-testid={`branch-orders-${b.referral_code}`}><ShoppingBag className="w-3.5 h-3.5" /> Orders</Link>
        <Link to={link("earnings")} className={act} data-testid={`branch-earnings-${b.referral_code}`}><IndianRupee className="w-3.5 h-3.5" /> Earnings</Link>
        {b.status === "pending_payment" && <button onClick={() => onPay(b)} className="btn-primary text-xs py-1.5" data-testid={`branch-activate-${b.referral_code}`}><CreditCard className="w-3.5 h-3.5" /> Activate Branch</button>}
        {b.status === "pending_payment" && !b.is_primary && <button onClick={() => onCancel(b.id)} className={`${act} text-red-600`} data-testid={`branch-cancel-${b.referral_code}`}><X className="w-3.5 h-3.5" /> Cancel</button>}
      </div>
    </div>
  );
};

export const PageLoader = () => <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" /></div>;
